import { isUuid } from '@joyful/utils'
import { matchPath } from 'react-router-dom'
import type { NavbarApp } from '../../../components/navbar/Navbar'
import type { HasFeatureFlag } from '../../../hooks/useHasFeatureFlag'
import type { HasCapability } from '../../../hooks/useHasUserCapability'
import {
  EncounterTimelineSelectionQueryMap,
  getEncounterListTimelineHref,
} from '../../encounters/utils/encounterListTimelineHref'
import type { GetDefaultAppRoutePathOptions } from './appRouteRegistry'
import {
  findVisibleRegistryNavigationItem,
  getDefaultAppRoutePath,
} from './appRouteRegistry'

export interface GetAppSwitchRoutePathOptions extends GetDefaultAppRoutePathOptions {
  hash?: string
  pathname: string
  search?: string
}

type AppSurfaceMatch =
  | { key: string; orgSlug: string | null; type: 'registry' }
  | { analysisId: string | null; orgSlug: string | null; type: 'analysis' }
  | { orgSlug: string; taskId: string; type: 'call-task' }
  | { encounterId: string; orgSlug: string; type: 'encounter-detail' }
  | { orgSlug: string; subpath: string | null; type: 'phone-subpath' }
  | {
      key: 'projects' | 'requests' | 'work-blocks'
      orgSlug: string
      subpath: string
      type: 'registry-subpath'
    }

export function getAppSwitchRoutePath({
  app,
  hasCapability,
  hasFeatureFlag,
  hash = '',
  orgSlug,
  pathname,
  search = '',
}: GetAppSwitchRoutePathOptions) {
  const matchedSurface = matchCurrentAppSurface({
    orgSlug,
    pathname,
    search,
  })
  const counterpartPath = matchedSurface
    ? resolveAppSurfaceCounterpartPath({
        app,
        hasCapability,
        hasFeatureFlag,
        hash,
        matchedSurface,
        search,
      })
    : null

  return (
    counterpartPath ??
    getDefaultAppRoutePath({
      app,
      hasCapability,
      hasFeatureFlag,
      orgSlug: matchedSurface?.orgSlug ?? orgSlug,
    })
  )
}

function matchCurrentAppSurface({
  orgSlug,
  pathname,
  search,
}: {
  orgSlug: string | null
  pathname: string
  search: string
}): AppSurfaceMatch | null {
  return (
    matchEncounterAppSurface({ pathname, search }) ??
    matchEntityDetailAppSurface(pathname) ??
    matchPhoneOrBlocksAppSurface(pathname) ??
    matchAnalysisAppSurface({ orgSlug, pathname }) ??
    matchRegistryAppSurface({ orgSlug, pathname })
  )
}

function matchEncounterAppSurface({
  pathname,
  search,
}: {
  pathname: string
  search: string
}): AppSurfaceMatch | null {
  return (
    matchEncounterDetailPathname(pathname) ??
    matchEncountersListAppSurface({ pathname, search })
  )
}

function matchEncounterDetailPathname(
  pathname: string,
): AppSurfaceMatch | null {
  const appEncounterDetailMatch = matchPath(
    '/:orgSlug/encounters/:encounterId',
    pathname,
  )
  const appEncounterId = appEncounterDetailMatch?.params.encounterId
  const appEncounterOrgSlug = appEncounterDetailMatch?.params.orgSlug
  if (appEncounterOrgSlug && appEncounterId && isUuid(appEncounterId)) {
    return {
      encounterId: appEncounterId,
      orgSlug: appEncounterOrgSlug,
      type: 'encounter-detail',
    }
  }

  const shortEncounterDetailMatch = matchPath(
    '/:orgSlug/:encounterId',
    pathname,
  )
  const shortEncounterId = shortEncounterDetailMatch?.params.encounterId
  const shortEncounterOrgSlug = shortEncounterDetailMatch?.params.orgSlug
  if (
    shortEncounterOrgSlug &&
    !isReservedTopLevelSegment(shortEncounterOrgSlug) &&
    shortEncounterId &&
    isUuid(shortEncounterId)
  ) {
    return {
      encounterId: shortEncounterId,
      orgSlug: shortEncounterOrgSlug,
      type: 'encounter-detail',
    }
  }

  return null
}

function matchEncountersListAppSurface({
  pathname,
  search,
}: {
  pathname: string
  search: string
}): AppSurfaceMatch | null {
  const encountersListMatch = matchPath('/:orgSlug/encounters', pathname)
  const encountersListOrgSlug = encountersListMatch?.params.orgSlug
  if (!encountersListOrgSlug) {
    return null
  }

  const searchParams = new URLSearchParams(search)
  const timelineEncounterId =
    EncounterTimelineSelectionQueryMap.encounter_id.parseServerSide(
      searchParams.get('encounter_id') ?? undefined,
    )
  if (timelineEncounterId) {
    return {
      encounterId: timelineEncounterId,
      orgSlug: encountersListOrgSlug,
      type: 'encounter-detail',
    }
  }

  return {
    key: 'encounters',
    orgSlug: encountersListOrgSlug,
    type: 'registry',
  }
}

function isReservedTopLevelSegment(segment: string) {
  return segment === 'analysis' || segment === 'internal' || segment === 'logic'
}

const ENTITY_DETAIL_PATTERNS: ReadonlyArray<{
  key: 'projects' | 'requests'
  paramName: string
  pattern: string
  segment: 'projects' | 'requests'
}> = [
  {
    key: 'projects',
    paramName: 'projectId',
    pattern: '/:orgSlug/projects/:projectId',
    segment: 'projects',
  },
  {
    key: 'requests',
    paramName: 'practiceRequestId',
    pattern: '/:orgSlug/requests/:practiceRequestId',
    segment: 'requests',
  },
]

function matchEntityDetailAppSurface(pathname: string): AppSurfaceMatch | null {
  for (const route of ENTITY_DETAIL_PATTERNS) {
    const match = matchPath(route.pattern, pathname)
    const entityId = match?.params[route.paramName]
    if (match?.params.orgSlug && entityId && isUuid(entityId)) {
      return {
        key: route.key,
        orgSlug: match.params.orgSlug,
        subpath: `/${route.segment}/${entityId}`,
        type: 'registry-subpath',
      }
    }
  }

  const callTaskMatch = matchPath('/:orgSlug/call-tasks/:taskId', pathname)
  const taskId = callTaskMatch?.params.taskId
  if (callTaskMatch?.params.orgSlug && taskId && isUuid(taskId)) {
    return {
      orgSlug: callTaskMatch.params.orgSlug,
      taskId,
      type: 'call-task',
    }
  }

  return null
}

function matchPhoneOrBlocksAppSurface(
  pathname: string,
): AppSurfaceMatch | null {
  const workBlocksDateMatch = matchPath('/:orgSlug/blocks/:date', pathname)
  if (workBlocksDateMatch?.params.orgSlug && workBlocksDateMatch.params.date) {
    return {
      key: 'work-blocks',
      orgSlug: workBlocksDateMatch.params.orgSlug,
      subpath: `/blocks/${workBlocksDateMatch.params.date}`,
      type: 'registry-subpath',
    }
  }

  const workBlocksMatch = matchPath('/:orgSlug/blocks', pathname)
  if (workBlocksMatch?.params.orgSlug) {
    return {
      key: 'work-blocks',
      orgSlug: workBlocksMatch.params.orgSlug,
      subpath: '/blocks/today',
      type: 'registry-subpath',
    }
  }

  const phoneSubpathMatch = matchPath('/:orgSlug/phone/:subpath', pathname)
  if (
    phoneSubpathMatch?.params.orgSlug &&
    phoneSubpathMatch.params.subpath &&
    isSupportedPhoneSubpath(phoneSubpathMatch.params.subpath)
  ) {
    return {
      orgSlug: phoneSubpathMatch.params.orgSlug,
      subpath: phoneSubpathMatch.params.subpath,
      type: 'phone-subpath',
    }
  }

  const phoneMatch = matchPath('/:orgSlug/phone', pathname)
  if (phoneMatch?.params.orgSlug) {
    return {
      orgSlug: phoneMatch.params.orgSlug,
      subpath: null,
      type: 'phone-subpath',
    }
  }

  return null
}

const ANALYSIS_DETAIL_ROUTE_PATTERNS: ReadonlyArray<{
  hasOrgSlug: boolean
  pattern: string
}> = [
  { hasOrgSlug: true, pattern: '/:orgSlug/internal/analysis/:analysisId' },
  { hasOrgSlug: false, pattern: '/internal/analysis/:analysisId' },
  { hasOrgSlug: true, pattern: '/:orgSlug/library/:analysisId' },
  { hasOrgSlug: true, pattern: '/:orgSlug/analysis/:analysisId' },
  { hasOrgSlug: false, pattern: '/analysis/:analysisId' },
]

const ANALYSIS_LIST_ROUTE_PATTERNS: ReadonlyArray<{
  hasOrgSlug: boolean
  pattern: string
}> = [
  { hasOrgSlug: true, pattern: '/:orgSlug/internal/analysis' },
  { hasOrgSlug: false, pattern: '/internal/analysis' },
  { hasOrgSlug: true, pattern: '/:orgSlug/library' },
]

function matchAnalysisAppSurface({
  orgSlug,
  pathname,
}: {
  orgSlug: string | null
  pathname: string
}): AppSurfaceMatch | null {
  for (const route of ANALYSIS_DETAIL_ROUTE_PATTERNS) {
    const match = matchPath(route.pattern, pathname)
    const analysisId = match?.params.analysisId
    const matchedOrgSlug = route.hasOrgSlug ? match?.params.orgSlug : orgSlug
    if (match && analysisId && isUuid(analysisId)) {
      return {
        analysisId,
        orgSlug: matchedOrgSlug ?? null,
        type: 'analysis',
      }
    }
  }

  for (const route of ANALYSIS_LIST_ROUTE_PATTERNS) {
    const match = matchPath(route.pattern, pathname)
    if (match) {
      const matchedOrgSlug = route.hasOrgSlug ? match.params.orgSlug : orgSlug
      return {
        analysisId: null,
        orgSlug: matchedOrgSlug ?? null,
        type: 'analysis',
      }
    }
  }

  return null
}

const REGISTRY_SURFACE_PATTERNS: ReadonlyArray<{
  end?: boolean
  hasOrgSlug: boolean
  key: string
  pattern: string
}> = [
  { hasOrgSlug: true, key: 'projects', pattern: '/:orgSlug/projects' },
  { hasOrgSlug: true, key: 'requests', pattern: '/:orgSlug/requests' },
  {
    hasOrgSlug: true,
    key: 'biller-assignments',
    pattern: '/:orgSlug/members/assignments',
  },
  {
    hasOrgSlug: true,
    key: 'uncollectible',
    pattern: '/:orgSlug/uncollectible',
  },
  { hasOrgSlug: true, key: 'dropbox', pattern: '/:orgSlug/dropbox' },
  { hasOrgSlug: true, key: 'files', pattern: '/:orgSlug/files/tree' },
  { hasOrgSlug: true, key: 'files', pattern: '/:orgSlug/files' },
  {
    hasOrgSlug: true,
    key: 'files',
    pattern: '/internal/filesystem/:orgSlug',
  },
  {
    hasOrgSlug: true,
    key: 'payer-directory',
    pattern: '/:orgSlug/phone/payer-directory',
  },
  {
    hasOrgSlug: true,
    key: 'dashboard',
    pattern: '/:orgSlug/internal/dashboard',
  },
  { hasOrgSlug: false, key: 'dashboard', pattern: '/internal/dashboard' },
  {
    end: false,
    hasOrgSlug: true,
    key: 'dashboard',
    pattern: '/:orgSlug/dashboard',
  },
  {
    hasOrgSlug: false,
    key: 'announcements',
    pattern: '/internal/announcements',
  },
  {
    hasOrgSlug: true,
    key: 'announcements',
    pattern: '/:orgSlug/announcements',
  },
]

function matchRegistryAppSurface({
  orgSlug,
  pathname,
}: {
  orgSlug: string | null
  pathname: string
}): AppSurfaceMatch | null {
  for (const route of REGISTRY_SURFACE_PATTERNS) {
    const match = matchPath(
      { end: route.end ?? true, path: route.pattern },
      pathname,
    )
    const matchedOrgSlug = match?.params.orgSlug
    const isTopLevelOrgSlug = route.pattern.startsWith('/:orgSlug/')
    if (
      match &&
      (!isTopLevelOrgSlug ||
        !matchedOrgSlug ||
        !isReservedTopLevelSegment(matchedOrgSlug))
    ) {
      return {
        key: route.key,
        orgSlug: route.hasOrgSlug ? (matchedOrgSlug ?? null) : orgSlug,
        type: 'registry',
      }
    }
  }

  return null
}

function isSupportedPhoneSubpath(subpath: string) {
  return (
    subpath === 'calls' ||
    subpath === 'messages' ||
    subpath === 'tasks' ||
    subpath === 'queue' ||
    subpath === 'monitor'
  )
}

function resolveAppSurfaceCounterpartPath({
  app,
  hasCapability,
  hasFeatureFlag,
  hash,
  matchedSurface,
  search,
}: {
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  hash: string
  matchedSurface: AppSurfaceMatch
  search: string
}): string | null {
  switch (matchedSurface.type) {
    case 'encounter-detail':
      return resolveEncounterDetailCounterpartPath({
        app,
        encounterId: matchedSurface.encounterId,
        hasCapability,
        hasFeatureFlag,
        hash,
        orgSlug: matchedSurface.orgSlug,
        search,
      })

    case 'registry-subpath': {
      const parentItem = findVisibleRegistryNavigationItem({
        app,
        hasCapability,
        hasFeatureFlag,
        key: matchedSurface.key,
        orgSlug: matchedSurface.orgSlug,
      })
      return parentItem?.path
        ? `/${matchedSurface.orgSlug}${matchedSurface.subpath}${search}${hash}`
        : null
    }

    case 'phone-subpath':
      return resolvePhoneSubpathCounterpartPath({
        app,
        hasCapability,
        hasFeatureFlag,
        hash,
        orgSlug: matchedSurface.orgSlug,
        search,
        subpath: matchedSurface.subpath,
      })

    case 'call-task':
      return resolveCallTaskCounterpartPath({
        app,
        hasCapability,
        hasFeatureFlag,
        hash,
        orgSlug: matchedSurface.orgSlug,
        search,
        taskId: matchedSurface.taskId,
      })

    case 'analysis':
      return resolveAnalysisCounterpartPath({
        analysisId: matchedSurface.analysisId,
        app,
        hasCapability,
        hasFeatureFlag,
        orgSlug: matchedSurface.orgSlug,
        search,
      })

    case 'registry':
      return resolveRegistrySurfaceCounterpartPath({
        app,
        hasCapability,
        hasFeatureFlag,
        hash,
        key: matchedSurface.key,
        orgSlug: matchedSurface.orgSlug,
        search,
      })
  }
}

function resolveEncounterDetailCounterpartPath({
  app,
  encounterId,
  hasCapability,
  hasFeatureFlag,
  hash,
  orgSlug,
  search,
}: {
  app: NavbarApp
  encounterId: string
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  hash: string
  orgSlug: string
  search: string
}) {
  if (app === 'workspace' || app === 'bpo') {
    if (!hasCapability('encounter.details.view')) {
      return null
    }

    const cleanedSearch = stripTimelineEncounterIdFromSearch(search)
    return `/${orgSlug}/${encounterId}${cleanedSearch}${hash}`
  }

  if (app === 'app') {
    const canOpenPracticeEncounterDetails =
      hasFeatureFlag('enable_practice_encounter_details') &&
      hasCapability('encounter.details.view')
    if (canOpenPracticeEncounterDetails) {
      const cleanedSearch = stripTimelineEncounterIdFromSearch(search)
      return `/${orgSlug}/encounters/${encounterId}${cleanedSearch}${hash}`
    }

    if (hasCapability('encounter.list.view')) {
      return getEncounterListTimelineHref({ encounterId, orgSlug })
    }
  }

  return null
}

function stripTimelineEncounterIdFromSearch(search: string) {
  if (!search) {
    return ''
  }

  const searchParams = new URLSearchParams(search)
  if (!searchParams.has('encounter_id')) {
    return search
  }

  searchParams.delete('encounter_id')
  const serialized = searchParams.toString()
  return serialized ? `?${serialized}` : ''
}

function resolvePhoneSubpathCounterpartPath({
  app,
  hasCapability,
  hasFeatureFlag,
  hash,
  orgSlug,
  search,
  subpath,
}: {
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  hash: string
  orgSlug: string
  search: string
  subpath: string | null
}) {
  const phoneItem = findVisibleRegistryNavigationItem({
    app,
    hasCapability,
    hasFeatureFlag,
    key: 'phone',
    orgSlug,
  })
  if (!phoneItem?.path) {
    return null
  }

  if (
    subpath === 'tasks' &&
    (!hasCapability('phone_call_task.view') ||
      !hasFeatureFlag('phone_call_tasks'))
  ) {
    return phoneItem.path
  }

  return subpath
    ? `/${orgSlug}/phone/${subpath}${search}${hash}`
    : `${phoneItem.path}${search}${hash}`
}

function resolveCallTaskCounterpartPath({
  app,
  hasCapability,
  hasFeatureFlag,
  hash,
  orgSlug,
  search,
  taskId,
}: {
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  hash: string
  orgSlug: string
  search: string
  taskId: string
}) {
  const isTargetPhoneApp = app === 'workspace' || app === 'bpo'
  const canViewCallTasks =
    hasCapability('phone_call_task.view') && hasFeatureFlag('phone_call_tasks')
  return isTargetPhoneApp && canViewCallTasks
    ? `/${orgSlug}/call-tasks/${taskId}${search}${hash}`
    : null
}

function resolveAnalysisCounterpartPath({
  analysisId,
  app,
  hasCapability,
  hasFeatureFlag,
  orgSlug,
  search,
}: {
  analysisId: string | null
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  orgSlug: string | null
  search: string
}) {
  // Workspace has no analysis surface; it forwards `internal/analysis` paths to the internal app.
  if (app === 'internal' || app === 'workspace') {
    return resolveArtifactsPath({
      analysisId,
      hasCapability,
      orgSlug,
      search,
    })
  }

  if (app === 'app') {
    const libraryItem = findVisibleRegistryNavigationItem({
      app: 'app',
      hasCapability,
      hasFeatureFlag,
      key: 'library',
      orgSlug,
    })
    if (analysisId && orgSlug && hasCapability('app.app.view.full')) {
      return `/${orgSlug}/analysis/${analysisId}`
    }

    if (
      analysisId &&
      hasCapability('app.app.view.full') &&
      hasCapability('analysis.view.unscoped')
    ) {
      return `/analysis/${analysisId}`
    }

    return libraryItem?.path ?? null
  }

  return null
}

function resolveArtifactsPath({
  analysisId,
  hasCapability,
  orgSlug,
  search,
}: {
  analysisId: string | null
  hasCapability: HasCapability
  orgSlug: string | null
  search: string
}) {
  const canViewArtifacts =
    hasCapability('analysis.view') && hasCapability('app.internal.view')
  if (!canViewArtifacts) {
    return null
  }

  const basePath = orgSlug
    ? `/${orgSlug}/internal/analysis`
    : '/internal/analysis'
  return analysisId ? `${basePath}/${analysisId}${search}` : basePath
}

function resolveRegistrySurfaceCounterpartPath({
  app,
  hasCapability,
  hasFeatureFlag,
  hash,
  key,
  orgSlug,
  search,
}: {
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  hash: string
  key: string
  orgSlug: string | null
  search: string
}) {
  const candidateKeys = getEquivalentRegistryKeys(key)
  for (const candidateKey of candidateKeys) {
    const navigationItem = findVisibleRegistryNavigationItem({
      app,
      hasCapability,
      hasFeatureFlag,
      key: candidateKey,
      orgSlug,
    })
    if (navigationItem?.path) {
      if (shouldPreserveRegistrySurfaceQuery({ candidateKey, key })) {
        return `${navigationItem.path}${search}${hash}`
      }

      const defaultSearch = navigationItem.search ?? ''
      return `${navigationItem.path}${defaultSearch}`
    }
  }

  return null
}

function getEquivalentRegistryKeys(key: string): ReadonlyArray<string> {
  if (key === 'dropbox' || key === 'files') {
    return ['dropbox', 'files']
  }

  return [key]
}

function shouldPreserveRegistrySurfaceQuery({
  candidateKey,
  key,
}: {
  candidateKey: string
  key: string
}) {
  if (candidateKey !== key) {
    return false
  }

  return (
    key === 'encounters' ||
    key === 'projects' ||
    key === 'requests' ||
    key === 'uncollectible' ||
    key === 'biller-assignments' ||
    key === 'dropbox' ||
    key === 'files' ||
    key === 'phone'
  )
}
